import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useToast } from '../../components/Toast';
import { AsyncButton } from '../../components/AsyncButton';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i=0; i<raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) throw new Error('Service Worker non supportato');
  // if not registered yet, register now
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (!existing) {
    await navigator.serviceWorker.register('/sw.js');
  }
  return navigator.serviceWorker.ready;
}

export function SettingsPage() {
  const { push } = useToast();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');

  const [pushEnabled, setPushEnabled] = useState(false);
  const [busyPush, setBusyPush] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!('serviceWorker' in navigator)) return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushEnabled(!!sub);
      } catch (_) {}
    })();
  }, []);

  async function changePassword() {
    if (newPassword !== newPassword2) {
      push({ title: 'Le password non coincidono' });
      return;
    }
    try {
      await api.post('/api/auth/change-password', { oldPassword, newPassword });
      setOldPassword(''); setNewPassword(''); setNewPassword2('');
      push({ title: 'Password aggiornata' });
    } catch (e:any) {
      push({ title: 'Errore', body: e?.data?.error || e?.message });
    }
  }

  async function enablePush() {
    const vapid = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
    if (!vapid) {
      push({ title: 'VAPID non configurato', body: 'Imposta VITE_VAPID_PUBLIC_KEY nel frontend e VAPID_* nel backend.' });
      return;
    }

    const perm = await Notification.requestPermission();
    if (perm !== 'granted') {
      push({ title: 'Permesso notifiche negato' });
      return;
    }

    const reg = await ensureServiceWorker();

    // already subscribed?
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid)
      });
    }

    await api.post('/api/notifications/subscribe', { subscription: sub.toJSON() });
    setPushEnabled(true);
    push({ title: 'Notifiche attive' });
  }

  async function disablePush() {
    const reg = await ensureServiceWorker();
    const sub = await reg.pushManager.getSubscription();
    if (!sub) {
      setPushEnabled(false);
      return;
    }
    try {
      await api.post('/api/notifications/unsubscribe', { endpoint: sub.endpoint });
    } catch (_) {
      // even if backend fails, try to remove locally
    }
    await sub.unsubscribe();
    setPushEnabled(false);
    push({ title: 'Notifiche disattivate' });
  }

  async function togglePush() {
    if (busyPush) return;
    setBusyPush(true);
    try {
      if (pushEnabled) await disablePush();
      else await enablePush();
    } catch (e:any) {
      push({ title: 'Errore notifiche', body: e?.data?.error || e?.message });
    } finally {
      setBusyPush(false);
    }
  }

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="card">
        <div className="h2" style={{ marginTop:0 }}>Impostazioni</div>

        <div className="h3">Cambia password</div>
        <div className="grid" style={{ gap: 10 }}>
          <input className="input" type="password" placeholder="Password attuale" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
          <input className="input" type="password" placeholder="Nuova password (8+, 1 maiuscola, 1 numero)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <input className="input" type="password" placeholder="Conferma nuova password" value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)} />
          <AsyncButton className="btn btnPrimary" onClickAsync={changePassword} busyText="Salvo…">Aggiorna</AsyncButton>
        </div>
      </div>

      <div className="card">
        <div className="h3">Notifiche push</div>
        <div className="small">Ricevi avvisi per pausa {'>'} 60 minuti, lavoro {'>'} 8 ore e nuovi messaggi.</div>
        <div style={{ marginTop: 12, display:'flex', gap: 10, flexWrap:'wrap', alignItems:'center' }}>
          <AsyncButton
            className={`btn ${pushEnabled ? 'btnSecondary' : 'btnPrimary'}`}
            onClickAsync={togglePush}
            disabled={busyPush}
            busyText="Aggiorno…"
          >
            {pushEnabled ? 'Disattiva notifiche' : 'Attiva notifiche'}
          </AsyncButton>
          <span className="badge badgeGray">{pushEnabled ? 'Attive' : 'Non attive'}</span>
        </div>
        <div className="small" style={{ marginTop: 10 }}>
          Nota: le notifiche push richiedono HTTPS (oppure localhost) e un browser compatibile.
        </div>
      </div>

      <div className="card">
        <div className="h3">Suggerimento PWA</div>
        <div className="small">Per un’esperienza più simile a un’app, aggiungi questa web app alla schermata Home dal menu del browser.</div>
      </div>
    </div>
  );
}
